import React, { useCallback, useEffect, useState } from 'react'
import { mergeLeft } from 'merge-left-utils'
import { Button, DateRangePicker, Form, SelectPicker, Tag } from 'rsuite'
import { RSUITE_DATEPICKER_LOCALE } from '@@partner:config/rsuite'
import { useFetchAllShops } from '@@partner:graphql/query/shop/allShops.gql'
import useSearchParams from '@@partner:helpers/hooks/useSearchParams'
import type { GQL_ID, NestedFilter } from '@@partner:types:graphql'
import { prepareCourierWorkShiftFilter, type CourierWorkShiftFiltersType } from '@@partner:helpers/courierWorkshift'

import './ShiftsFilter.scss'
import SelectCourierPicker from '@components/common/commonFormInput/SelectCourierPicker'
import { SHIFT_STATUSES } from '@@partner:helpers/formatters/workshift'

export type ShiftsFiltersType = CourierWorkShiftFiltersType

type ShiftsFilterProps = {
  onFilterChanged: (filters: NestedFilter) => void
}

const defaultFilter: ShiftsFiltersType = {
  dateRange: null,
  courierIds: [],
  shopIds: [],
  status: [],
  vehicleIds: [],
  vehicleTypeIds: []
}

export default function ShiftsFilter ({ onFilterChanged }: ShiftsFilterProps) {
  const { getSearchParams, setSearchParams } = useSearchParams()
  const [ state, setState ] = useState<ShiftsFiltersType>(defaultFilter)

  const { data: shopsData, loading: shopsLoading } = useFetchAllShops({
    pager: { page: 1, limit: 100 }
  })

  const shopOptions = React.useMemo(() => {
    if (!shopsData?.allShops?.edges) return []
    
    return shopsData.allShops.edges.map((shop: any) => ({
      label: shop.name,
      value: shop.id
    }))
  }, [ shopsData ])

  useEffect(() => {
    const searchParams = getSearchParams()
    const newState: ShiftsFiltersType = mergeLeft(defaultFilter, searchParams)
    setState(newState)
    onFilterChanged(prepareCourierWorkShiftFilter(newState))
  }, [ ])

  const handleFormSubmit = useCallback(() => {
    setSearchParams(state)
    onFilterChanged(prepareCourierWorkShiftFilter(state))
  }, [ onFilterChanged, state, setSearchParams ])

  const handleFormChange = (formValue: Partial<ShiftsFiltersType>) => {
    setState((state) => mergeLeft(state, formValue))
  }

  const handleDateRangeChange = useCallback((range: [Date, Date] | null) => {
    setState(prev => ({ ...prev, dateRange: range }))
  }, [])

  return (
    <Form
      formValue={state}
      className="ShiftsFilter"
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
    >
      <Form.Group className="ShiftsFilter__Filters">
        <DateRangePicker
          value={state.dateRange}
          onChange={handleDateRangeChange}
          locale={RSUITE_DATEPICKER_LOCALE}
          format="dd.MM.yyyy"
          placeholder="Выберите период"
          size="md"
          cleanable
        />
        <SelectCourierPicker
          isMultiple={true}
          value={state.courierIds}        
          onChange={(value) => {
            setState(prev => ({ ...prev, courierIds: value || undefined }))
          }}
        />

        <SelectPicker
          data={shopOptions}
          value={state.shopIds}
          onChange={(value) => setState(prev => ({ ...prev, shopId: value || undefined }))}
          placeholder="Все магазины"
          size="md"
          style={{ width: 200 }}
          loading={shopsLoading}
          cleanable
          searchable
        />

        <SelectPicker
          data={SHIFT_STATUSES}
          value={state.status?.[0]}
          onChange={(value) => setState(prev => ({ ...prev, status: value ? [ value ] : [] }))}
          placeholder="Все статусы"
          valueKey="value"
          labelKey="label"
          size="md"
          style={{ width: 150 }}
          cleanable
          renderMenuItem={(label, item: any) => (
            <Tag color={item?.color}>
              {label}
            </Tag>
          )}
        />
      </Form.Group>

      <div className="ShiftsFilter__Controls">
        <Button
          onClick={handleFormSubmit}
          size="md"
          color="green"
          appearance="primary"
        >
          Поиск
        </Button>
      </div>
    </Form>
  )
}
